/**
 * Tenant Management Component
 *
 * Provides a comprehensive interface for Global Admins to manage tenants:
 * - List all tenants with search, filtering, and pagination
 * - Create new tenants
 * - Edit existing tenants
 * - Activate/deactivate tenants
 * - Delete tenants (with confirmation)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';
import { TenantForm, type TenantData } from './TenantForm';

// =============================================================================
// Types
// =============================================================================

/**
 * Tenant with stats
 */
interface TenantWithStats {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  settings: unknown;
  createdAt: string;
  updatedAt: string;
  stats?: {
    userCount: number;
    botCount: number;
    activeBotCount: number;
  };
}

/**
 * API response for tenant list
 */
interface TenantsResponse {
  tenants: TenantWithStats[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * Delete preview response
 */
interface DeletePreviewResponse {
  id: string;
  name: string;
  resourceCounts: {
    userCount: number;
    botCount: number;
    resourceCount: number;
  };
  warning: string;
}

/**
 * Filter state
 */
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Modal state
 */
type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; tenant: TenantWithStats }
  | { type: 'delete'; tenant: TenantWithStats; preview?: DeletePreviewResponse };

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS = {
  active: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  inactive: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
} as const;

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ active }: { active: boolean }) {
  const colors = active ? STATUS_COLORS.active : STATUS_COLORS.inactive;

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {active ? 'Active' : 'Inactive'}
    </div>
  );
}

/**
 * Tenant card component
 */
interface TenantCardProps {
  tenant: TenantWithStats;
  onEdit: (tenant: TenantWithStats) => void;
  onToggleActive: (tenant: TenantWithStats) => void;
  onDelete: (tenant: TenantWithStats) => void;
  isLoading: boolean;
}

function TenantCard({
  tenant,
  onEdit,
  onToggleActive,
  onDelete,
  isLoading,
}: TenantCardProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onEdit(tenant)}
            className="font-semibold text-white hover:text-discord-blurple transition-colors truncate block text-left"
          >
            {tenant.name}
          </button>
          <p className="mt-1 text-sm text-gray-400 font-mono truncate">{tenant.slug}</p>
        </div>
        <StatusBadge active={tenant.active} />
      </div>

      {tenant.stats && (
        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
          <span>{tenant.stats.userCount} users</span>
          <span>{tenant.stats.botCount} bots</span>
          {tenant.stats.activeBotCount > 0 && (
            <span className="text-green-400">{tenant.stats.activeBotCount} running</span>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Created: {new Date(tenant.createdAt).toLocaleDateString()}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(tenant)}
            disabled={isLoading}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              tenant.active
                ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30'
                : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
            }`}
            title={tenant.active ? 'Deactivate tenant' : 'Activate tenant'}
          >
            {tenant.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onEdit(tenant)}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit tenant"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => onDelete(tenant)}
            disabled={isLoading}
            className="rounded-md bg-red-600/20 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete tenant"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tenant row component for table view
 */
interface TenantRowProps {
  tenant: TenantWithStats;
  onEdit: (tenant: TenantWithStats) => void;
  onToggleActive: (tenant: TenantWithStats) => void;
  onDelete: (tenant: TenantWithStats) => void;
  isLoading: boolean;
}

function TenantRow({
  tenant,
  onEdit,
  onToggleActive,
  onDelete,
  isLoading,
}: TenantRowProps) {
  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <button
            onClick={() => onEdit(tenant)}
            className="font-medium text-white hover:text-discord-blurple transition-colors text-left"
          >
            {tenant.name}
          </button>
          <span className="text-sm text-gray-500 font-mono">{tenant.slug}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge active={tenant.active} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {tenant.stats?.userCount ?? 0}
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {tenant.stats?.botCount ?? 0}
        {tenant.stats?.activeBotCount ? (
          <span className="text-green-400 ml-1">({tenant.stats.activeBotCount} running)</span>
        ) : null}
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {new Date(tenant.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(tenant)}
            disabled={isLoading}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              tenant.active
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {tenant.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onEdit(tenant)}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(tenant)}
            disabled={isLoading}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Status filter buttons component
 */
interface StatusFilterProps {
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

function StatusFilterButtons({ value, onChange, counts }: StatusFilterProps) {
  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="flex items-center gap-2">
      {filters.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
          {counts[key] > 0 && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === key ? 'bg-white/20' : 'bg-discord-darker'
              }`}
            >
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Empty state component
 */
function EmptyState({
  filter,
  onCreateClick,
}: {
  filter: StatusFilter;
  onCreateClick: () => void;
}) {
  const messages: Record<StatusFilter, { title: string; description: string }> = {
    all: {
      title: 'No tenants yet',
      description: 'Create your first tenant to start managing organizations',
    },
    active: {
      title: 'No active tenants',
      description: 'All tenants are currently inactive',
    },
    inactive: {
      title: 'No inactive tenants',
      description: 'All tenants are currently active',
    },
  };

  const { title, description } = messages[filter];

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      {filter === 'all' && (
        <button
          onClick={onCreateClick}
          className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
        >
          Create Tenant
        </button>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 bg-discord-light rounded" />
        <div className="h-8 w-32 bg-discord-light rounded" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-5 w-32 bg-discord-light rounded" />
                <div className="h-4 w-48 bg-discord-light rounded" />
              </div>
              <div className="h-6 w-16 bg-discord-light rounded-full" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="h-4 w-24 bg-discord-light rounded" />
              <div className="flex gap-2">
                <div className="h-8 w-16 bg-discord-light rounded" />
                <div className="h-8 w-8 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Modal component
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
        />
        <div className="relative w-full max-w-lg rounded-lg bg-discord-dark border border-discord-light p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Delete confirmation modal content
 */
function DeleteConfirmation({
  tenant,
  preview,
  onConfirm,
  onCancel,
  isLoading,
}: {
  tenant: TenantWithStats;
  preview?: DeletePreviewResponse;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-red-900/30 border border-red-500/50 p-4">
        <div className="flex items-start">
          <svg
            className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-red-300">
              Permanently delete tenant?
            </h4>
            <p className="mt-1 text-sm text-red-200/80">
              This action cannot be undone. All data associated with this tenant will be
              permanently deleted.
            </p>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-300">
        <p>
          You are about to delete <strong className="text-white">{tenant.name}</strong>
        </p>
      </div>

      {preview && (
        <div className="rounded-md bg-discord-darker border border-discord-light p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Resources to be deleted:</h4>
          <ul className="text-sm text-gray-400 space-y-1">
            <li className="flex items-center gap-2">
              <span className="font-mono text-red-400">{preview.resourceCounts.userCount}</span>
              <span>users</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-mono text-red-400">{preview.resourceCounts.botCount}</span>
              <span>bots</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-mono text-red-400">{preview.resourceCounts.resourceCount}</span>
              <span>resource assignments</span>
            </li>
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Deleting...' : 'Delete Tenant'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Tenant management page component
 *
 * Provides comprehensive tenant management for Global Admins:
 * - List tenants with filtering and search
 * - Create, edit, activate/deactivate, and delete tenants
 */
export function TenantManagement() {
  useAuth(); // Ensure user is authenticated
  const { isGlobalAdmin } = useTenant();

  const [tenants, setTenants] = useState<TenantWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  /**
   * Fetch tenants from API
   */
  const fetchTenants = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: '20',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (searchQuery) {
        queryParams.set('search', searchQuery);
      }

      if (statusFilter !== 'all') {
        queryParams.set('active', statusFilter === 'active' ? 'true' : 'false');
      }

      const result = await apiClient.get<TenantsResponse>(`/tenants?${queryParams}`);

      if (isSuccess(result)) {
        setTenants(result.data.tenants);
        setPagination({
          total: result.data.pagination.total,
          totalPages: result.data.pagination.totalPages,
          hasMore: result.data.pagination.hasMore,
        });
      } else {
        setError(result.error.message || 'Failed to fetch tenants');
      }
    } catch (err) {
      setError('Failed to load tenants. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [page, searchQuery, statusFilter]);

  /**
   * Toggle tenant active status
   */
  const handleToggleActive = useCallback(async (tenant: TenantWithStats) => {
    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();
    const endpoint = tenant.active
      ? `/tenants/${tenant.id}/deactivate`
      : `/tenants/${tenant.id}/reactivate`;

    try {
      const result = await apiClient.post<TenantWithStats>(endpoint);

      if (isSuccess(result)) {
        setTenants((prev) =>
          prev.map((t) =>
            t.id === tenant.id ? { ...t, active: !tenant.active } : t
          )
        );
      } else {
        setError(result.error.message || 'Failed to update tenant status');
      }
    } catch (err) {
      setError('Failed to update tenant. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, []);

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = useCallback(async (tenant: TenantWithStats) => {
    setModal({ type: 'delete', tenant });

    // Fetch delete preview
    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<DeletePreviewResponse>(
        `/tenants/${tenant.id}/preview-delete`
      );

      if (isSuccess(result)) {
        setModal({ type: 'delete', tenant, preview: result.data });
      }
    } catch {
      // Continue without preview
    }
  }, []);

  /**
   * Delete tenant
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (modal.type !== 'delete') return;

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.delete<{ id: string; deleted: boolean }>(
        `/tenants/${modal.tenant.id}`
      );

      if (isSuccess(result)) {
        setTenants((prev) => prev.filter((t) => t.id !== modal.tenant.id));
        setModal({ type: 'none' });
      } else {
        setError(result.error.message || 'Failed to delete tenant');
      }
    } catch (err) {
      setError('Failed to delete tenant. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [modal]);

  /**
   * Handle form submission (create/edit)
   */
  const handleFormSubmit = useCallback(
    (data: TenantData) => {
      if (modal.type === 'create') {
        // Add new tenant to list
        setTenants((prev) => [
          {
            ...data,
            id: data.id!,
            settings: data.settings ?? null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          ...prev,
        ]);
      } else if (modal.type === 'edit') {
        // Update existing tenant
        setTenants((prev) =>
          prev.map((t) =>
            t.id === data.id
              ? {
                  ...t,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : t
          )
        );
      }
      setModal({ type: 'none' });
    },
    [modal]
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchTenants();
  }, [fetchTenants]);

  /**
   * Calculate status counts
   */
  const statusCounts = useMemo(() => {
    return {
      all: tenants.length,
      active: tenants.filter((t) => t.active).length,
      inactive: tenants.filter((t) => !t.active).length,
    };
  }, [tenants]);

  /**
   * Filter tenants by search (client-side for current page)
   */
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;

    const query = searchQuery.toLowerCase();
    return tenants.filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(query) ||
        tenant.slug.toLowerCase().includes(query)
    );
  }, [tenants, searchQuery]);

  // Access control check
  if (!isGlobalAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg
            className="h-12 w-12 text-red-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-lg font-medium text-white">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-400">
            You must be a Global Admin to access tenant management.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Tenant Management</h1>
            <p className="mt-1 text-gray-400">
              Manage organizations and their access to the platform
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Tenant
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Filters and Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <StatusFilterButtons
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
              />

              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search tenants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 rounded-md border border-discord-light bg-discord-darker py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md bg-discord-light">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded-l-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`rounded-r-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'table'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => void fetchTenants()}
                  disabled={isLoading}
                  className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tenant List */}
            {filteredTenants.length === 0 ? (
              <EmptyState
                filter={statusFilter}
                onCreateClick={() => setModal({ type: 'create' })}
              />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredTenants.map((tenant) => (
                  <TenantCard
                    key={tenant.id}
                    tenant={tenant}
                    onEdit={(t) => setModal({ type: 'edit', tenant: t })}
                    onToggleActive={handleToggleActive}
                    onDelete={handleDeleteClick}
                    isLoading={isActionLoading}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-discord-light overflow-hidden">
                <table className="w-full">
                  <thead className="bg-discord-darker">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Users
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Bots
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-discord-dark">
                    {filteredTenants.map((tenant) => (
                      <TenantRow
                        key={tenant.id}
                        tenant={tenant}
                        onEdit={(t) => setModal({ type: 'edit', tenant: t })}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDeleteClick}
                        isLoading={isActionLoading}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  Page {page} of {pagination.totalPages} ({pagination.total} tenants)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasMore}
                    className="rounded-md bg-discord-light px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Showing {filteredTenants.length} of {tenants.length} tenants
              </span>
              <span>
                {statusCounts.active} active · {statusCounts.inactive} inactive
              </span>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal.type === 'create' || modal.type === 'edit'}
        onClose={() => setModal({ type: 'none' })}
        title={modal.type === 'create' ? 'Create New Tenant' : 'Edit Tenant'}
      >
        <TenantForm
          tenant={modal.type === 'edit' ? {
            id: modal.tenant.id,
            name: modal.tenant.name,
            slug: modal.tenant.slug,
            active: modal.tenant.active,
            settings: modal.tenant.settings as Record<string, unknown> | undefined,
          } : null}
          onSubmit={handleFormSubmit}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={isActionLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modal.type === 'delete'}
        onClose={() => setModal({ type: 'none' })}
        title="Delete Tenant"
      >
        {modal.type === 'delete' && (
          <DeleteConfirmation
            tenant={modal.tenant}
            preview={modal.preview}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setModal({ type: 'none' })}
            isLoading={isActionLoading}
          />
        )}
      </Modal>
    </Layout>
  );
}

export default TenantManagement;
